import {Suspense} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'

import type {Channels} from '../hooks/use-mergeability-live-updates'
import {useMergeboxStatusCalculator} from '../hooks/use-mergebox-status-calculator'
import type {MergeBoxStatusCalculator} from '../helpers/merge-box-status-calculator/merge-box-status-calculator'
import {
  reviewStatusPresentation,
  checksStatusPresentation,
  conflictsStatusPresentation,
  draftStatusPresentation,
  type CondensedStatusRow,
} from '../helpers/condensed-merge-status-presentation'
import type {StatusChecksPageData} from '../page-data/payloads/status-checks'
import styles from './CondensedMergeStatus.module.css'
import {CondensedMergeStatusErrorFallback} from './CondensedMergeStatusErrorFallback'
import {CondensedMergeStatusSkeleton} from './CondensedMergeStatusSkeleton'

interface CondensedMergeStatusProps {
  headSha: string
  channels: Channels | null
}

export function CondensedMergeStatusWithSuspense(props: CondensedMergeStatusProps) {
  return (
    <ErrorBoundary boundaryName="CondensedMergeStatus" fallback={<CondensedMergeStatusErrorFallback />}>
      <Suspense fallback={<CondensedMergeStatusSkeleton />}>
        <CondensedMergeStatus {...props} />
      </Suspense>
    </ErrorBoundary>
  )
}

/**
 * A condensed merge status component that displays the overall mergeability status of a pull request.
 *
 * Designed to be used in the sidebar of the pull request overview page.
 *
 * This is prototype and wider adoption is not recommended at this time.
 * @experimental
 */
export function CondensedMergeStatus({headSha, channels}: CondensedMergeStatusProps) {
  const {calculator, pullRequest, statusChecksData} = useMergeboxStatusCalculator({headSha, channels})

  // Don't render for closed or merged PRs
  if (pullRequest.state === 'CLOSED' || pullRequest.state === 'MERGED') {
    return null
  }

  return <CondensedMergeStatusContent calculator={calculator} statusChecksData={statusChecksData} />
}

function CondensedMergeStatusContent({
  calculator,
  statusChecksData,
}: {
  calculator: MergeBoxStatusCalculator
  statusChecksData: StatusChecksPageData
}) {
  const {
    DraftStateSection: draftSection,
    ReviewerSection: reviewerSection,
    ChecksSection: checksSection,
    ConflictsSection: conflictsSection,
  } = calculator.sections

  const rows: CondensedStatusRow[] = []

  if (draftSection.shouldRender) {
    rows.push(draftStatusPresentation(draftSection))
  }
  if (reviewerSection.shouldRender) {
    rows.push(reviewStatusPresentation(reviewerSection))
  }
  if (checksSection.shouldRender) {
    rows.push(checksStatusPresentation(checksSection, statusChecksData))
  }
  if (conflictsSection.shouldRender) {
    rows.push(conflictsStatusPresentation(conflictsSection))
  }

  if (rows.length === 0) return null

  return (
    <ul className={styles.statusList}>
      {rows.map(row => (
        <li key={row.label} className={styles.statusRow}>
          <span style={{color: row.iconColor, display: 'flex'}}>
            <row.icon size={16} />
          </span>
          <span className={styles.statusText}>
            {row.label}
            {row.detail && (
              <>
                {' '}
                <span className={styles.statusDetail}>{row.detail}</span>
              </>
            )}
          </span>
        </li>
      ))}
    </ul>
  )
}
